import _ from 'lodash';
import { AzureBlobAPI } from "./azure-blob-api";
import { FileFunctions } from './file-functions';

export class FileApi {

    constructor(instance, inProgress, accounts, forceRefresh) {
        this.azureBlobApi = new AzureBlobAPI(instance, inProgress, accounts, forceRefresh);
    }

    uploadFiles = async (companyName, path, collection, progressIndicatorCallback) => {
        // Keep a stable originalSize for each file so callers can compute progress
        // percentages. Do not rely on callers to re-derive originalSize from the
        // progress updates because the update objects we pass here may omit it.
        const dataToUploadDetails = _.map(collection, c => { return { name: c.name, remainingBytes: c.size, originalSize: c.size } });

        const tokenPromises = [];
        for (const file of collection) {
            let filePath = FileFunctions.cleanBlobPath(`${path}/${file.name.split(':')[0]}`);
            tokenPromises.push(this.azureBlobApi.getSASToken(file, filePath, (2 | 4 | 8), companyName));
        }

    const tokenResponses = await Promise.all(tokenPromises);
    console.log('FileApi.uploadFiles: received token responses', { tokenCount: tokenResponses.length, companyName, path });
        
        const uploadPromises = [];
        for (const tokenResponse of tokenResponses) {
            tokenResponse.progressCallback = (file, remaining) => {
                const entry = _.find(dataToUploadDetails, f => f.name === file.name);
                if (entry) entry.remainingBytes = remaining;
                // Emit a debug log so developers can see progress callbacks fire
                // even when the UI updates very quickly.
                try {
                    console.debug('FileApi.uploadFiles: progress', { name: file.name, remainingBytes: remaining, originalSize: entry && entry.originalSize });
                } catch (e) {
                    // swallow console errors
                }
                // Pass a shallow clone of the array so callers don't accidentally
                // mutate our internal tracking objects (and so React sees a new
                // reference each update).
                try {
                    progressIndicatorCallback(_.cloneDeep(dataToUploadDetails));
                } catch (e) {
                    // If the caller's callback throws, avoid breaking the upload
                    // loop; just log and continue.
                    console.warn('FileApi.uploadFiles: progress callback failed', e);
                }
            };
            uploadPromises.push(this.azureBlobApi.uploadFile(tokenResponse));
        }

    const uploadResponses = await Promise.all(uploadPromises);
    console.log('FileApi.uploadFiles: uploadResponses', { uploadCount: uploadResponses.length });

        // Attach SAS-tokened urls (if provided by the token responses) so callers
        // can immediately show blob-hosted previews without waiting for a server
        // side refresh.
        const enriched = uploadResponses.map((r, i) => {
            const tokenResult = tokenResponses[i] && tokenResponses[i].result ? tokenResponses[i].result : {};
            return Object.assign({}, r, {
                pathIncSASToken: tokenResult.pathToBlobIncSasToken || null,
                thumbPathIncSASToken: tokenResult.thumbPathToBlobIncSasToken || tokenResult.pathToBlobIncSasToken || null
            });
        });

    console.log('FileApi.uploadFiles: enriched responses', enriched.map(e => ({ file: e.file && e.file.name, path: e.path, pathIncSASToken: !!e.pathIncSASToken })));

        return enriched;
    };

    getFileStructure = async (showDeletedItems) => {
        try {
            const structure = await this.azureBlobApi.getFileStructure(showDeletedItems);
            var cleansedStructure = FileFunctions.convertJsonObjectToCleanFileStructure(structure.root.contents, structure.root);
            structure.root = cleansedStructure;
            return structure;
        } catch (error) {
            console.error("Error obtaining folder data", error);
            throw error;
        }
    };

    queueFileDeletion = async (deleteFileModel) => {
        try {
            await this.azureBlobApi.queueFileDeletion(deleteFileModel);
            return true;
        } catch (error) {
            console.log("Error queueing deletion of file", error);
            throw error;
        }
    };

    queueFileVersionDeletion = async (deleteVersionModel) => {
        try {
            await this.azureBlobApi.queueFileVersionDeletion(deleteVersionModel);
            return true;
        } catch (error) {
            console.log("Error queueing deletion of version", error);
            throw error;
        }
    };

    promoteToCurrentVersion = async (promoteModel) => {
        try {
            await this.azureBlobApi.promoteToCurrentVersion(promoteModel);
            return true;
        } catch (error) {
            console.log("Error queueing promotion of version", error);
            throw error;
        }
    };

}